import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  ContentCopyOutlined as CopyIcon,
  CheckCircleOutline as CopiedIcon,
} from "@mui/icons-material";

export interface SelectableRow {
  id: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  meta?: React.ReactNode;
}

interface SelectableCopyListProps {
  rows: SelectableRow[];
  alreadyCopiedIds: string[];
  loading: boolean;
  emptyMessage: string;
  onCopy: (selectedIds: string[]) => void;
  isCopying: boolean;
  ctaLabel?: string;
  /** Called when selection changes — parent can use to compute auto-selected IDs */
  getAutoSelectIds?: (selectedIds: string[]) => string[];
}

const SelectableCopyList: React.FC<SelectableCopyListProps> = ({
  rows,
  alreadyCopiedIds,
  loading,
  emptyMessage,
  onCopy,
  isCopying,
  ctaLabel = "Copy selected",
  getAutoSelectIds,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const copiedSet = useMemo(
    () => new Set(alreadyCopiedIds),
    [alreadyCopiedIds],
  );

  // Compute auto-selected IDs (e.g. workflow dependencies)
  const autoSelected = useMemo(() => {
    if (!getAutoSelectIds) return new Set<string>();
    const ids = getAutoSelectIds(Array.from(selected));
    return new Set(ids.filter((id) => !copiedSet.has(id) && !selected.has(id)));
  }, [getAutoSelectIds, selected, copiedSet]);

  const effectiveSelected = useMemo(() => {
    const merged = new Set(selected);
    for (const id of autoSelected) merged.add(id);
    return merged;
  }, [selected, autoSelected]);

  const selectableRows = rows.filter((r) => !copiedSet.has(r.id));
  const allSelectable = selectableRows.length > 0;
  const allSelected =
    allSelectable && selectableRows.every((r) => selected.has(r.id));

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableRows.map((r) => r.id)));
    }
  };

  const handleCopy = () => {
    if (effectiveSelected.size === 0) return;
    onCopy(Array.from(effectiveSelected));
    setSelected(new Set());
  };

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
        <CircularProgress size={18} />
        <Typography variant="body2">Loading...</Typography>
      </Stack>
    );
  }

  if (rows.length === 0) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Checkbox
            checked={allSelected}
            indeterminate={selected.size > 0 && !allSelected}
            onChange={toggleAll}
            disabled={!allSelectable || isCopying}
          />
          <Typography variant="body2" color="text.secondary">
            {effectiveSelected.size > 0
              ? `${effectiveSelected.size} selected${autoSelected.size > 0 ? ` (${autoSelected.size} auto-included)` : ""}`
              : `${selectableRows.length} available, ${copiedSet.size} already copied`}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          size="small"
          startIcon={isCopying ? <CircularProgress size={14} /> : <CopyIcon />}
          onClick={handleCopy}
          disabled={effectiveSelected.size === 0 || isCopying}
        >
          {isCopying ? "Copying..." : ctaLabel}
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {rows.map((row, i) => {
          const isCopied = copiedSet.has(row.id);
          const isSelected = selected.has(row.id);
          const isAuto = autoSelected.has(row.id);
          return (
            <Stack
              key={row.id}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                px: 2,
                py: 1.25,
                borderTop: i === 0 ? "none" : "1px solid",
                borderColor: "divider",
                bgcolor: isCopied
                  ? "action.hover"
                  : isSelected || isAuto
                    ? "action.selected"
                    : "transparent",
              }}
            >
              <Checkbox
                checked={isSelected || isAuto}
                disabled={isCopied || isCopying || isAuto}
                onChange={() => toggleOne(row.id)}
                size="small"
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    color: isCopied ? "text.secondary" : "text.primary",
                  }}
                >
                  {row.primary}
                </Typography>
                {row.secondary && (
                  <Typography variant="caption" color="text.secondary">
                    {row.secondary}
                  </Typography>
                )}
              </Box>
              {row.meta}
              {isAuto && (
                <Chip
                  label="Auto-included"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
              {isCopied && (
                <Chip
                  icon={<CopiedIcon fontSize="small" />}
                  label="Copied"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>
          );
        })}
      </Paper>
    </Stack>
  );
};

export default SelectableCopyList;
